/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { getInventoryRecord, hasInStock, useInventoryArray } from '@/data/Content/_Inventory';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { requisitionListItemUpdate } from '@/data/Content/_RequisitionList';
import { wishListUpdater } from '@/data/Content/_WishListDetails';
import { addToCartFetcher, BASE_ADD_2_CART_BODY, useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { getAttrsByIdentifier, useProductDetails } from '@/data/Content/ProductDetails';
import { EventsContext } from '@/data/context/events';
import { useLocalization } from '@/data/Localization';
import { dFix, useSettings } from '@/data/Settings';
import { ShippingMode } from '@/data/types/AllowedShipMode';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { BundleTableRowData, ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { getBundleComponentOrSkuAttributes } from '@/data/utils/getBundleComponentOrSkuAttributes';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { processError } from '@/data/utils/processError';
import { SelectChangeEvent } from '@mui/material';
import { WishlistWishlistItem } from 'integration/generated/transactions/data-contracts';
import { isEqual } from 'lodash';
import { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const EMPTY_SKUS: ProductType[] = [];
const mapBundleData = (components: ProductType[], availability: ProductAvailabilityData[]) =>
	components.map((comp, rowNumber) => {
		const { sKUs = EMPTY_SKUS, quantity } = comp;
		const { attrStates } = getBundleComponentOrSkuAttributes(comp);
		const isOneSku = sKUs.length === 1;

		return {
			...(isOneSku ? sKUs[0] : comp),
			seo: comp.seo,
			rowNumber,
			isOneSku,
			attrStates,
			quantity,
			availability,
		} as BundleTableRowData;
	});

const generateOrderItem = (
	selectedSize: BundleSelectedSize,
	availability: ProductAvailabilityData[],
	storeName: string,
	pickupInStoreShipMode: ShippingMode | undefined
) => {
	const { quantity, partNumber, productId } = selectedSize;
	const online = getInventoryRecord(availability, partNumber);
	const offline = storeName ? getInventoryRecord(availability, partNumber, storeName) : undefined;
	const inventory = hasInStock(online, dFix(quantity, 0)) ? online : offline;
	return {
		productId,
		partNumber,
		quantity: quantity.toString(),
		...(inventory?.physicalStoreId && {
			physicalStoreId: inventory.physicalStoreId,
			shipModeId: pickupInStoreShipMode?.shipModeId,
		}),
	};
};

type Props = {
	pdp: ReturnType<typeof useProductDetails>;
	physicalStoreName: string;
};

const EMPTY_COMPS: ProductType[] = [];
const EMPTY = [] as ProductAvailabilityData[];
const EMPTY_PRODUCT = {} as ProductType;

export type BundleSelectedSize = {
	mainPartNumber: string;
	mainProductId: string;
	partNumber: string;
	productId: string;
	quantity: number;
};

export const useBundleDetailsTable = ({ pdp, physicalStoreName }: Props) => {
	const { onAddToCart } = useContext(EventsContext);
	const { addNItemsSuc } = useLocalization('success-message');
	const messages = useLocalization('productDetail');
	const router = useNextRouter();

	const { product = EMPTY_PRODUCT, loginRequired, category } = pdp;
	const { components = EMPTY_COMPS } = product;
	const { pickupInStoreShipMode: pickupMode } = useAllowableShippingModes();
	const { showSuccessMessage, showErrorMessage, notifyError, showAddToCart } = useNotifications();
	const { mutateCart } = useCart();
	const { settings } = useSettings();
	const { langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const partNumbers = useMemo(() => {
		if (!components.length) {
			return [];
		}

		return components
			.map((comp) => {
				const items = comp?.sKUs ?? [];

				return items.map((item) => item.partNumber);
			})
			.flat();
	}, [components]);

	const { availability = EMPTY } = useInventoryArray(partNumbers, physicalStoreName);

	const componentsKUsCleaned = useMemo(
		() =>
			components.reduce<ProductType[]>((acc, item) => {
				const sKUs = item.sKUs?.reduce<ProductType[]>((accSku, sku) => {
					const availabilityItem = availability.find(
						(availabilityItem) => availabilityItem.productId === sku.uniqueID
					);
					if (availabilityItem && availabilityItem?.inventoryStatus === 'Available') {
						accSku.push(sku);
					}
					return accSku;
				}, []);

				acc.push({
					...item,
					sKUs,
				});

				return acc;
			}, []),
		[components, availability]
	);

	const [data, setData] = useState<BundleTableRowData[]>([]);
	const [error, setError] = useState<{ message?: string }>();
	const [selectedSize, setSelectedSize] = useState<BundleSelectedSize[]>([]);

	const isSelectedSizeValid = useMemo(() => {
		if (componentsKUsCleaned.length === 0 || selectedSize.length === 0) {
			return false;
		}

		const allProductsHaveSkuSelected = componentsKUsCleaned.every((sku) =>
			selectedSize.some((size) => size.mainProductId === sku.uniqueID)
		);

		return allProductsHaveSkuSelected;
	}, [selectedSize, componentsKUsCleaned]);

	/**
	 * Add the selected product and its quantities to the shopping cart
	 */
	const addToCart = useCallback(
		async (event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			if (loginRequired()) {
				return;
			}

			if (isSelectedSizeValid) {
				setError({});
				const orderItem = selectedSize
					.filter(({ quantity }) => dFix(quantity, 0))
					.map((record) => generateOrderItem(record, availability, physicalStoreName, pickupMode));

				const body = { ...BASE_ADD_2_CART_BODY, orderItem };
				try {
					await addToCartFetcher(true)(settings?.storeId ?? '', {}, body, params);
					await mutateCart();

					// notification
					showAddToCart();

					data
						.filter(({ quantity }) => dFix(quantity, 0))
						.forEach((record) => {
							const quantity = dFix(record.quantity, 0);
							const sku = record.selectedSku ?? record;
							onAddToCart({
								gtm: {
									selection: {
										sku: { ...sku, type: 'bundle' },
										attrsByIdentifier: getAttrsByIdentifier(sku.definingAttributes),
										quantity,
										buyable: true,
									},
									category,
									quantity,
									orgName: '', // TODO: specify selected org-name
									orgId: '', // TODO: specify selected org
									storeName: settings.storeName,
									settings,
								},
							});
						});

					scrollTo(0, 0);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[
			loginRequired,
			isSelectedSizeValid,
			data,
			availability,
			physicalStoreName,
			pickupMode,
			settings,
			params,
			mutateCart,
			showAddToCart,
			notifyError,
			category,
			onAddToCart,
			selectedSize,
		]
	);

	const addToWishList = useCallback(
		(_partNumber: string, _quantity: number, wishList: WishlistWishlistItem) => async () => {
			const selections = data.filter(
				({ isOneSku, selectedSku, quantity }) => dFix(quantity, 0) && (isOneSku || selectedSku)
			);
			if (selections.length) {
				setError({});
				const item = selections.map(({ selectedSku, partNumber, quantity: quantityRequested }) => ({
					partNumber: selectedSku?.partNumber ?? partNumber,
					quantityRequested,
				})) as any[];
				const rc = await wishListUpdater(true)(
					settings?.storeId as string,
					wishList.uniqueID as string,
					{ item },
					{ addItem: true },
					params
				);
				if (rc) {
					showSuccessMessage(addNItemsSuc.t({ n: item.length }));
				}

				// TODO: add onAddToWishlist event call
				// product && onAddToWishlist(product, quantity);
			} else {
				setError({ message: messages.selectSomething.t() });
			}
		},
		[data, settings?.storeId, params, showSuccessMessage, addNItemsSuc, messages]
	);

	const addToRequisitionList = useCallback(
		(requisitionListId: string) => async () => {
			const selections = data.filter(
				({ isOneSku, selectedSku, quantity }) => dFix(quantity, 0) && (isOneSku || selectedSku)
			);
			if (selections.length) {
				await Promise.all(
					selections.map(({ selectedSku, partNumber, quantity }) =>
						requisitionListItemUpdate(true)(
							{
								storeId: settings.storeId,
								langId,
								requisitionListId,
								data: {
									partNumber: selectedSku?.partNumber ?? partNumber,
									quantity: dFix(quantity, 0),
								},
							},
							params
						)
					)
				);
				showSuccessMessage(addNItemsSuc.t({ n: selections.length }));
			} else {
				showErrorMessage(messages.addToCartErrorMsg.t());
			}
		},
		[
			addNItemsSuc,
			data,
			messages,
			params,
			settings.storeId,
			showErrorMessage,
			showSuccessMessage,
			langId,
		]
	);

	const onQuantity = (rowNumber: number, value: string) => {
		setData((old) => {
			old[rowNumber] = { ...old[rowNumber], quantity: value };
			return [...old];
		});
	};

	const onSizeSelect = useCallback((newSelectedSize: BundleSelectedSize) => {
		setSelectedSize((old) => {
			const searchMainProduct = old.find(
				(size) => size.mainProductId === newSelectedSize.mainProductId
			);

			if (searchMainProduct) {
				return old.filter((size) => size.mainProductId !== newSelectedSize.mainProductId);
			}

			return [...old, newSelectedSize];
		});
	}, []);

	const onAttributeSelect = useCallback(
		(attrName: string, record: BundleTableRowData) => (event: SelectChangeEvent) => {
			const { attrStates, rowNumber, sKUs = [] } = record;
			const attrValueIdentifier = event.target.value;

			// find a sku with the same attributes as those selected
			const check = { ...attrStates, [attrName]: attrValueIdentifier };
			const sku = sKUs.find(({ definingAttributes }) => {
				const skuAttrs = definingAttributes.reduce(
					(all, attr) => ({ ...all, [attr.identifier]: attr.values[0]?.identifier }),
					{}
				);

				return isEqual(check, skuAttrs);
			});

			setData((old) => {
				const selectedSku = sku ? { ...sku } : undefined;
				const attrStates = { ...old[rowNumber].attrStates, [attrName]: attrValueIdentifier };
				old[rowNumber] = { ...old[rowNumber], attrStates, selectedSku };
				return [...old];
			});
		},
		[]
	);

	useEffect(
		() => setData(mapBundleData(componentsKUsCleaned, availability)),
		[componentsKUsCleaned, availability]
	);

	return {
		data,
		physicalStoreName,
		addToCart,
		onQuantity,
		onAttributeSelect,
		error,
		addToWishList,
		addToRequisitionList,
		availability,
		components,
		selectedSize,
		onSizeSelect,
		readyToAddToCart: isSelectedSizeValid,
		componentsKUsCleaned,
	};
};
